import type { CalendarApi, DateInput } from '@fullcalendar/core';
import type FullCalendar from '@fullcalendar/react';
import { useAbsences, useResourceUsers } from '@src/query';
import type { Absence } from '@src/types';
import { addMonths } from '@src/utils';
import { useCallback, useContext, useMemo } from 'react';
import { ABS_DAY_VIEW, ABS_HALF_MONTH_VIEW, ABS_MONTH_VIEW } from './constants';
import { AbsenceTimelineContext } from './context';
import type { TimelineSlot } from './types';
import {
  getAbsenceSelectionById,
  getUserWorktimeForDate,
  prepareAbsencePayload,
} from './utils';

type CalendarController = {
  next: () => void;
  prev: () => void;
  today: () => void;
  getDate: () => Date | null;
  changeView: (...args: Parameters<CalendarApi['changeView']>) => void;
  gotoDate: (...args: Parameters<CalendarApi['gotoDate']>) => void;
  setMonthView: (year: number, month: number) => void;
  setHalfMonthView: (year: number, monthIndex: number) => void;
  setDayView: (date: DateInput) => void;
  setOption: (...args: Parameters<CalendarApi['setOption']>) => void;
};

function useTimelineContext() {
  const context = useContext(AbsenceTimelineContext);
  if (!context) {
    throw new Error('AbsenceTimelineContext.Provider is missing');
  }
  return context;
}

export default function useAbsencesTimeline() {
  const { absences, bookAbsence, deleteAbsences } = useAbsences();
  const { users } = useResourceUsers();
  const timelineContext = useTimelineContext();
  const { view, selectedSlot, setSelectedSlot, calendarRef, ...restOfContext } =
    timelineContext;

  const attachCalendar = useCallback(
    (instance: FullCalendar | null) => {
      calendarRef.current = instance;
    },
    [calendarRef],
  );

  const getApi = useCallback(
    () => calendarRef.current?.getApi() ?? null,
    [calendarRef],
  );
  const calendar = useMemo<CalendarController>(
    () => ({
      next: () => getApi()?.next(),
      prev: () => getApi()?.prev(),
      today: () => getApi()?.today(),
      getDate: () => getApi()?.getDate() ?? null,
      changeView: (...args) => getApi()?.changeView(...args),
      gotoDate: (...args) => getApi()?.gotoDate(...args),
      setMonthView: (year, month) => {
        getApi()?.changeView(ABS_MONTH_VIEW, new Date(year, month, 1));
      },
      setHalfMonthView: (year, monthIndex) => {
        const start = new Date(year, monthIndex, 15);
        const end = addMonths(start, 1);
        getApi()?.changeView(ABS_HALF_MONTH_VIEW, start);
        getApi()?.setOption('visibleRange', { start, end });
      },
      setDayView: (date) => getApi()?.changeView(ABS_DAY_VIEW, date),
      setOption: (...args) => getApi()?.setOption(...args),
    }),
    [getApi],
  );

  const isMonthView = useMemo(() => view?.type === ABS_MONTH_VIEW, [view]);
  const isMonthOrHalfMonthView = useMemo(
    () => [ABS_HALF_MONTH_VIEW, ABS_MONTH_VIEW].includes(view?.type ?? ''),
    [view],
  );
  const isDayView = useMemo(() => view?.type === ABS_DAY_VIEW, [view]);

  const book = (values: Record<string, unknown>) => {
    return bookAbsence(prepareAbsencePayload(values), {
      onSuccess: () => setSelectedSlot(null),
    });
  };

  const remove = (ids: number[]) => {
    return deleteAbsences({ ids }, { onSuccess: () => setSelectedSlot(null) });
  };

  const selectedAbsencesById = useMemo<Map<number, Absence>>(
    () => new Map(absences.map((absence: Absence) => [absence.id, absence])),
    [absences],
  );

  const isDifferentAbsencesSelected = useCallback(
    (ids: number[] | null | undefined) => {
      if (!ids || ids.length <= 1) {
        return false;
      }

      let firstType: number | undefined;
      for (const id of ids) {
        const obj = selectedAbsencesById.get(id);
        if (!obj) {
          continue;
        }
        if (firstType === undefined) {
          firstType = obj.typeId;
        } else if (obj.typeId !== firstType) {
          return true;
        }
      }
      return false;
    },
    [selectedAbsencesById],
  );

  const getAbsenceTimeRange = useCallback(
    (slot: TimelineSlot) => {
      let startTime = slot.startTime;
      let endTime = slot.endTime;
      const userWorktime = getUserWorktimeForDate(
        slot.userId,
        users,
        slot.start,
      );
      if (userWorktime && !slot.startTime && !slot.endTime) {
        startTime = userWorktime.start;
        endTime = userWorktime.end;
      }
      return {
        startTime,
        endTime,
      };
    },
    [users],
  );

  const selectedSlotData = useMemo(() => {
    if (!selectedSlot) {
      return null;
    }

    const { selectedIds: selectedAbsencesIds, start, end } = selectedSlot;
    const selectedData = {
      start,
      end,
      typeId: null,
      ...(isDayView && getAbsenceTimeRange(selectedSlot)),
      tentative: false,
      notes: '',
    };

    if (
      isDifferentAbsencesSelected(selectedAbsencesIds) ||
      !selectedAbsencesIds?.length
    ) {
      // Just showing selected dates for different selected absences
      // or selected range
      return selectedData;
    }

    const selection = getAbsenceSelectionById(selectedAbsencesIds[0], absences);
    if (selection?.absenceIds.length) {
      const absenceId = selection.absenceIds[0];
      const absence = absences.find((a) => a.id === absenceId);
      if (absence) {
        return {
          ...selectedData,
          typeId: absence.typeId,
          tentative: absence.absenceTentative,
          notes: absence.notes,
        };
      }
    }
    return selectedData;
  }, [
    absences,
    selectedSlot,
    isDifferentAbsencesSelected,
    getAbsenceTimeRange,
    isDayView,
  ]);

  const timeline = {
    calendar,
    view,
    selectedSlot,
    setSelectedSlot,
    attachCalendar,
    ...restOfContext,
    absences,
    users,
    book,
    selectedSlotData,
    remove,
    isMonthView,
    isMonthOrHalfMonthView,
    isDayView,
  };
  return timeline;
}
